import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2,
  Calendar,
  User,
  Building,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Grid3X3,
  List,
  Download,
  FileSpreadsheet,
  FileText as FileTextIcon,
  FileDown,
  X
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { laceToolAPI, Assessment, reportsAPI } from '../../services/api';
import { authenticatedLaceToolAPI } from '../../services/authenticatedApi';
import { useApiAuth } from '../../hooks/useApiAuth';
import AssessmentDetailsModal from './AssessmentDetailsModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { SkeletonAssessmentsList } from '../Skeleton/SkeletonLoader';
import { Pagination as PaginationComponent } from '../Pagination';

const AssessmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const PageTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px;
  border: 1px solid #e5e7eb;
`;

const ViewToggleButton = styled.button`
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: none;

  &[aria-pressed='true'] {
    background: white;
    color: #3b82f6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    color: #3b82f6;
    background: #f9fafb;
  }

  &[aria-pressed='true']:hover {
    background: white;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const ExportDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ExportButton = styled.button`
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid #d1d5db;
  background: white;
  color: #374151;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const DropdownMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 180px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 4px;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    border-color: #3b82f6;
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);

    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
    }
  ` : `
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const FiltersSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FilterInput = styled.input`
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 16px;
`;

const FilterButton = styled.button`
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const ActiveFilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
`;

const RemoveFilterButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: #1e40af;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;

  &:hover {
    color: #1e3a8a;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const AssessmentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const AssessmentsListContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const ListHeader = styled.div`
  background: #f8fafc;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 120px;
  gap: 16px;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ListRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 120px;
  gap: 16px;
  align-items: center;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ListCell = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #111827;
`;

const PatientInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PatientNameList = styled.div`
  font-weight: 600;
  color: #111827;
`;

const FacilityNameList = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ScoreCell = styled.div<{ risk: 'low' | 'moderate' | 'high' | 'very-high' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const ScoreValue = styled.span`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
`;

const RiskBadgeList = styled.span<{ risk: 'low' | 'moderate' | 'high' | 'very-high' }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.risk) {
      case 'low':
        return 'background: #d1fae5; color: #065f46;';
      case 'moderate':
        return 'background: #fef3c7; color: #92400e;';
      case 'high':
        return 'background: #fed7aa; color: #9a3412;';
      case 'very-high':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const DateCell = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`;

const ListActionButton = styled.button<{ variant?: 'view' | 'edit' | 'delete' | 'download' }>`
  padding: 4px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    switch (props.variant) {
      case 'view':
        return `
          background: #dbeafe;
          color: #1e40af;
          &:hover { background: #bfdbfe; }
        `;
      case 'edit':
        return `
          background: #fef3c7;
          color: #92400e;
          &:hover { background: #fde68a; }
        `;
      case 'download':
        return `
          background: #dcfce7;
          color: #166534;
          &:hover { background: #bbf7d0; }
        `;
      case 'delete':
        return `
          background: #fee2e2;
          color: #991b1b;
          &:hover { background: #fecaca; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const AssessmentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border-color: #3b82f6;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const PatientInfo = styled.div`
  flex: 1;
`;

const PatientName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const FacilityName = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RiskBadge = styled.span<{ risk: 'low' | 'moderate' | 'high' | 'very-high' }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${props => {
    switch (props.risk) {
      case 'low':
        return 'background: #d1fae5; color: #065f46;';
      case 'moderate':
        return 'background: #fef3c7; color: #92400e;';
      case 'high':
        return 'background: #fed7aa; color: #9a3412;';
      case 'very-high':
        return 'background: #fee2e2; color: #991b1b;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const InfoLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #111827;
  font-weight: 600;
`;

const ScoreDisplay = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  margin: 8px 0;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const DateInfo = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant?: 'view' | 'edit' | 'delete' | 'download' }>`
  padding: 6px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    switch (props.variant) {
      case 'view':
        return `
          background: #dbeafe;
          color: #1e40af;
          &:hover { background: #bfdbfe; }
        `;
      case 'edit':
        return `
          background: #fef3c7;
          color: #92400e;
          &:hover { background: #fde68a; }
        `;
      case 'download':
        return `
          background: #dcfce7;
          color: #166534;
          &:hover { background: #bbf7d0; }
        `;
      case 'delete':
        return `
          background: #fee2e2;
          color: #991b1b;
          &:hover { background: #fecaca; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

const EmptyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #6b7280;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &[aria-current='page'] {
    background: #3b82f6;
    color: white;
  }

  &:hover {
    background: #f9fafb;
  }

  &[aria-current='page']:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface AssessmentFilters {
  search: string;
  facility: string;
  riskLevel: string;
  dateFrom: string;
  dateTo: string;
}

const AssessmentsList: React.FC = () => {
  const { getAccessToken } = useApiAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AssessmentFilters>({
    search: '',
    facility: '',
    riskLevel: '',
    dateFrom: '',
    dateTo: ''
  });
  const [appliedFilters, setAppliedFilters] = useState<AssessmentFilters>({
    search: '',
    facility: '',
    riskLevel: '',
    dateFrom: '',
    dateTo: ''
  });
  const [allAssessments, setAllAssessments] = useState<Assessment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [facilities, setFacilities] = useState<Array<{ id: string; name: string }>>([]);
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    totalPages: 1, currentPage: 1, itemsPerPage: 12
  });

  useEffect(() => { 
    fetchAssessments(1, itemsPerPage);
    fetchFacilities();
  }, []); 
  useEffect(() => { if (currentPage > 0) fetchAssessments(currentPage, itemsPerPage); }, [currentPage, itemsPerPage, appliedFilters]);

  

  useEffect(() => {
    if (!exportDropdownOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-export-dropdown]')) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [exportDropdownOpen]);

  const fetchAssessments = async (page: number, limit: number) => {
    try {
      setLoading(true);
      
      // Use authenticated API service with pagination and filters (excluding riskLevel which is client-side)
      const response = await authenticatedLaceToolAPI.getAssessments({
        page,
        limit,
        patientName: appliedFilters.search || undefined, 
        facilityName: appliedFilters.facility || undefined, 
        dateFrom: appliedFilters.dateFrom || undefined, 
        dateTo: appliedFilters.dateTo || undefined
      });
      
      let filteredAssessments = response.assessments || [];
      
      // Apply client-side risk level filter
      if (appliedFilters.riskLevel) {
        filteredAssessments = filteredAssessments.filter(assessment => {
          const riskLevel = getRiskLevel(assessment.totalScore);
          return riskLevel === appliedFilters.riskLevel;
        });
      }
      
      setAllAssessments(filteredAssessments);
      setAssessments(filteredAssessments);
      
      // Update pagination data
      const totalFiltered = appliedFilters.riskLevel 
        ? filteredAssessments.length 
        : (response.pagination?.totalItems || 0);
      
      setPaginationData({
        ...response.pagination,
        totalItems: totalFiltered,
        totalPages: Math.ceil(totalFiltered / limit) || 1
      });
    } catch (error) {
      console.error('Error fetching assessments:', error);
      // Set empty assessments on error
      setAssessments([]);
      setAllAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevel = (score: number): 'low' | 'moderate' | 'high' | 'very-high' => {
    if (score <= 4) return 'low';
    if (score <= 6) return 'moderate';
    if (score <= 9) return 'high';
    return 'very-high';
  };

  const getRiskLabel = (score: number): string => {
    if (score <= 4) return 'Low Risk';
    if (score <= 6) return 'Moderate Risk';
    if (score <= 9) return 'High Risk';
    return 'Very High Risk';
  };

  const getReadmissionProbability = (score: number): string => {
    const probabilityMap: { [key: number]: string } = {
      0: '0%',
      1: '1%',
      2: '2%',
      3: '3%',
      4: '4%',
      5: '5%',
      6: '6%',
      7: '7%',
      8: '9%',
      9: '10%',
      10: '12%',
      11: '14%',
      12: '17%',
      13: '20%',
      14: '23%',
      15: '27%',
      16: '30%',
      17: '35%',
      18: '39%',
      19: '44%'
    };
    
    // For scores above 19, use the highest known probability
    if (score >= 19) return '44%+';
    
    return probabilityMap[score] || 'Unknown';
  };

  const handleFilterChange = (key: keyof AssessmentFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const clearFilters = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const emptyFilters: AssessmentFilters = {
      search: '',
      facility: '',
      riskLevel: '',
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setCurrentPage(1);
  };

  const removeFilter = (key: keyof AssessmentFilters) => {
    const newFilters = { ...filters };
    newFilters[key] = '';
    setFilters(newFilters);
    
    const newAppliedFilters = { ...appliedFilters };
    newAppliedFilters[key] = '';
    setAppliedFilters(newAppliedFilters);
    setCurrentPage(1);
  };

  const fetchFacilities = async () => {
    try {
      const response = await reportsAPI.getFacilities();
      if (response.success) {
        setFacilities(response.data);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const handleViewAssessment = (assessment: Assessment) => {
    setSelectedAssessment(assessment);
  };

  const handleDeleteAssessment = async (assessment: Assessment) => {
    if (!window.confirm(`Are you sure you want to delete the assessment for ${assessment.patientName}? This action cannot be undone.`)) {
      return;
    }

    const loadingToast = toast.loading('Deleting assessment...');
    
    try {
      await authenticatedLaceToolAPI.deleteAssessment(assessment.id);
      toast.success('Assessment deleted successfully!', { id: loadingToast });
      
      // Refresh the assessments list
      // If we're on the last page and it becomes empty after deletion, go to previous page
      if (assessments.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchAssessments(currentPage, itemsPerPage);
      }
      
      // Close modal if the deleted assessment was selected
      if (selectedAssessment?.id === assessment.id) {
        setSelectedAssessment(null);
      }
    } catch (error: any) {
      console.error('Error deleting assessment:', error);
      
      let errorMessage = 'Failed to delete assessment. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          errorMessage = 'Assessment not found. It may have already been deleted.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to delete this assessment.';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  const handleDownloadPDF = async (assessment: Assessment) => {
    const loadingToast = toast.loading('Processing PDF download...');
    
    try {
      console.log('Starting PDF download for assessment:', assessment.id);
      
      const blob = await authenticatedLaceToolAPI.downloadAssessmentPDF(assessment.id);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty or invalid PDF file');
      }
      
      console.log('PDF blob received, size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const date = new Date(assessment.createdAt).toISOString().split('T')[0];
      const patientName = assessment.patientName.replace(/[^a-zA-Z0-9]/g, '_');
      link.download = `LACE_Assessment_${patientName}_${date}_${assessment.id}.pdf`;
      
      console.log('Downloading file:', link.download);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('PDF download completed successfully');
      toast.success('Assessment PDF download started!', { id: loadingToast });
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      
      let errorMessage = 'Failed to download PDF. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            break;
          case 404:
            errorMessage = 'Assessment not found. It may have been deleted.';
            break;
          case 500:
            errorMessage = 'Server error during PDF generation. Please try again later.';
            break;
          default:
            errorMessage = data?.message || `Server error (${status}). Please try again.`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || 'Unknown error occurred.';
      }
      
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  const handleCreateNew = () => {
    // Navigate to create new assessment
    window.location.href = '/assessments/new';
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const formatAssessmentForExport = (assessment: Assessment) => {
    const riskLevel = getRiskLevel(assessment.totalScore);
    const riskLabel = getRiskLabel(assessment.totalScore);
    const readmissionProbability = getReadmissionProbability(assessment.totalScore);
    
    return {
      'Patient Name': assessment.patientName,
      'Facility': assessment.facilityName,
      'Date of Birth': format(parseISO(assessment.dateOfBirth), 'MMM dd, yyyy'),
      'Admission Date': format(parseISO(assessment.admissionDate), 'MMM dd, yyyy'),
      'Length of Stay': assessment.lengthOfStay,
      'Length of Stay Points': assessment.lengthOfStayPoints,
      'Acute Admission': assessment.acuteAdmission,
      'Acute Admission Points': assessment.acuteAdmissionPoints,
      'Comorbidities': assessment.comorbidities ? assessment.comorbidities.join(', ') : '',
      'Comorbidity Points': assessment.comorbidityPoints,
      'Emergency Visits': assessment.emergencyVisits,
      'Emergency Visits Points': assessment.emergencyVisitsPoints,
      'Total LACE Score': assessment.totalScore,
      'Risk Level': riskLabel,
      'Readmission Probability': readmissionProbability,
      'Assessed By': assessment.assessedBy || 'Unknown',
      'Assessment Date': format(parseISO(assessment.createdAt), 'MMM dd, yyyy HH:mm'),
      'Last Updated': format(parseISO(assessment.updatedAt), 'MMM dd, yyyy HH:mm'),
      'Assessment ID': assessment.id,
      'Notes': assessment.notes || ''
    };
  };

  const hasActiveFilters = Object.values(appliedFilters).some(value => value && value.trim() !== '');

  const getExportAssessments = async (): Promise<Assessment[]> => {
    if (!hasActiveFilters) {
      return assessments;
    }

    const baseParams = {
      patientName: appliedFilters.search || undefined,
      facilityName: appliedFilters.facility || undefined,
      dateFrom: appliedFilters.dateFrom || undefined,
      dateTo: appliedFilters.dateTo || undefined,
      limit: itemsPerPage
    };

    const firstResponse = await authenticatedLaceToolAPI.getAssessments({
      ...baseParams,
      page: 1
    });
    let combined = firstResponse.assessments || [];
    const totalPages = firstResponse.pagination?.totalPages || 1;

    for (let page = 2; page <= totalPages; page += 1) {
      const pageResponse = await authenticatedLaceToolAPI.getAssessments({
        ...baseParams,
        page
      });
      combined = combined.concat(pageResponse.assessments || []);
    }

    if (appliedFilters.riskLevel) {
      combined = combined.filter(assessment => getRiskLevel(assessment.totalScore) === appliedFilters.riskLevel);
    }

    return combined;
  };

  const exportToExcel = async () => {
    const exportAssessments = await getExportAssessments();
    const exportData = exportAssessments.map(formatAssessmentForExport);
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assessments');
    
    const fileName = `lace-assessments-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    setExportDropdownOpen(false);
  };

  const exportToCSV = async () => {
    const exportAssessments = await getExportAssessments();
    const exportData = exportAssessments.map(formatAssessmentForExport);
    
    const csv = Papa.unparse(exportData, {
      header: true,
      delimiter: ','
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const fileName = `lace-assessments-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.csv`;
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setExportDropdownOpen(false);
  };

  const paginatedAssessments = assessments; const totalPages = paginationData.totalPages;

  const uniqueFacilities = facilities.length > 0 
    ? facilities.map(f => f.name)
    : Array.from(new Set(allAssessments.map(a => a.facilityName)));

  if (loading) {
    return <SkeletonAssessmentsList />;
  }

  return (
    <AssessmentsContainer>
      <HeaderSection>
        <PageTitle>Assessments</PageTitle>
        <ActionButtons>
          <ViewToggle>
            <ViewToggleButton
              aria-pressed={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <Grid3X3 size={16} />
              Grid
            </ViewToggleButton>
            <ViewToggleButton
              aria-pressed={viewMode === 'list'}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={16} />
              List
            </ViewToggleButton>
          </ViewToggle>
          
          <ExportDropdown data-export-dropdown>
            <ExportButton
              data-export-dropdown
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              title="Export Assessments"
            >
              <Download size={16} />
              Export
              <ChevronDown size={14} />
            </ExportButton>
            <DropdownMenu isOpen={exportDropdownOpen}>
              <DropdownItem onClick={exportToExcel}>
                <FileSpreadsheet size={16} />
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={exportToCSV}>
                <FileTextIcon size={16} />
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </ExportDropdown>
          
          <Button variant="primary" onClick={handleCreateNew}>
            <Plus size={16} />
            New Assessment
          </Button>
        </ActionButtons>
      </HeaderSection>

      <FiltersSection>
        <FiltersGrid>
          <FilterGroup>
            <FilterLabel>Search</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Search by patient or facility..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Facility</FilterLabel>
            <FilterSelect
              value={filters.facility}
              onChange={(e) => handleFilterChange('facility', e.target.value)}
            >
              <option value="">All Facilities</option>
              {uniqueFacilities.map(facility => (
                <option key={facility} value={facility}>{facility}</option>
              ))}
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Risk Level</FilterLabel>
            <FilterSelect
              value={filters.riskLevel}
              onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
            >
              <option value="">All Risk Levels</option>
              <option value="low">Low Risk</option>
              <option value="moderate">Moderate Risk</option>
              <option value="high">High Risk</option>
              <option value="very-high">Very High Risk</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date From</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date To</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>

        <FilterActions>
          <FilterButton type="button" onClick={applyFilters}>
            <Filter size={16} />
            Apply Filters
          </FilterButton>
          <ClearButton type="button" onClick={clearFilters}>
            <X size={16} />
            Clear All
          </ClearButton>
        </FilterActions>

        {Object.entries(appliedFilters).some(([key, value]) => value && value.trim() !== '') && (
          <ActiveFilters>
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (!value || value.trim() === '') return null;
              
              const displayKey = key === 'search' ? 'Search' :
                                 key === 'facility' ? 'Facility' :
                                 key === 'riskLevel' ? 'Risk Level' :
                                 key === 'dateFrom' ? 'Date From' :
                                 key === 'dateTo' ? 'Date To' : key;
              
              const displayValue = key === 'riskLevel' 
                ? value.charAt(0).toUpperCase() + value.slice(1).replace('-', ' ') + ' Risk'
                : value;
              
              return (
                <ActiveFilterTag key={key}>
                  {displayKey}: {displayValue}
                  <RemoveFilterButton onClick={() => removeFilter(key as keyof AssessmentFilters)}>
                    <X size={12} />
                  </RemoveFilterButton>
                </ActiveFilterTag>
              );
            })}
          </ActiveFilters>
        )}
      </FiltersSection>

      {assessments.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <FileText size={40} />
          </EmptyIcon>
          <EmptyTitle>No Assessments Found</EmptyTitle>
          <EmptyDescription>
            {assessments.length === 0 
              ? "Get started by creating your first LACE assessment."
              : "Try adjusting your filters to see more results."
            }
          </EmptyDescription>
          {assessments.length === 0 && (
            <Button variant="primary" onClick={handleCreateNew}>
              <Plus size={16} />
              Create First Assessment
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <AssessmentsGrid>
              {assessments.map((assessment) => {
                const riskLevel = getRiskLevel(assessment.totalScore);
                const riskLabel = getRiskLabel(assessment.totalScore);
                
                return (
                  <AssessmentCard
                    key={assessment.id}
                    onClick={() => handleViewAssessment(assessment)}
                  >
                    <CardHeader>
                      <PatientInfo>
                        <PatientName>{assessment.patientName}</PatientName>
                        <FacilityName>
                          <Building size={14} />
                          {assessment.facilityName}
                        </FacilityName>
                      </PatientInfo>
                      <RiskBadge risk={riskLevel}>
                        {riskLabel}
                      </RiskBadge>
                    </CardHeader>

                  <CardContent>
                    <ScoreDisplay>
                      Readmission Probability: {getReadmissionProbability(assessment.totalScore)}
                    </ScoreDisplay>

                    <InfoRow>
                      <InfoLabel>LACE Score:</InfoLabel>
                      <InfoValue style={{ color: '#0c4a6e', fontWeight: '700' }}>
                      {assessment.totalScore}
                      </InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Assessed By:</InfoLabel>
                      <InfoValue>{assessment.assessedBy || 'Unknown'}</InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Admission Date:</InfoLabel>
                      <InfoValue>
                        {format(parseISO(assessment.admissionDate), 'MMM dd, yyyy')}
                      </InfoValue>
                    </InfoRow>

                    <InfoRow>
                      <InfoLabel>Length of Stay:</InfoLabel>
                      <InfoValue>{assessment.lengthOfStay}</InfoValue>
                    </InfoRow>
                  </CardContent>

                    <CardFooter>
                      <DateInfo>
                        <Calendar size={12} />
                        {format(parseISO(assessment.createdAt), 'MMM dd, yyyy')}
                      </DateInfo>
                      <CardActionButtons>
                        <ActionButton
                          variant="view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAssessment(assessment);
                          }}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </ActionButton>
                        <ActionButton
                          variant="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/assessments/edit/${assessment.id}`);
                          }}
                          title="Edit Assessment"
                        >
                          <Edit size={14} />
                        </ActionButton>
                        <ActionButton
                          variant="download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPDF(assessment);
                          }}
                          title="Download PDF"
                        >
                          <FileDown size={14} />
                        </ActionButton>
                        <ActionButton
                          variant="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAssessment(assessment);
                          }}
                          title="Delete Assessment"
                        >
                          <Trash2 size={14} />
                        </ActionButton>
                      </CardActionButtons>
                    </CardFooter>
                  </AssessmentCard>
                );
              })}
            </AssessmentsGrid>
          ) : (
            <AssessmentsListContainer>
              <ListHeader>
                <div>Patient</div>
                <div>Score</div>
                <div>Risk Level</div>
                <div>Probability</div>
                <div>Assessed By</div>
                <div>Date</div>
                <div>Actions</div>
              </ListHeader>
              {assessments.map((assessment) => {
                const riskLevel = getRiskLevel(assessment.totalScore);
                const riskLabel = getRiskLabel(assessment.totalScore);
                
                return (
                  <ListRow
                    key={assessment.id}
                    onClick={() => handleViewAssessment(assessment)}
                  >
                    <ListCell>
                      <PatientInfoCell>
                        <PatientNameList>{assessment.patientName}</PatientNameList>
                        <FacilityNameList>
                          <Building size={12} />
                          {assessment.facilityName}
                        </FacilityNameList>
                      </PatientInfoCell>
                    </ListCell>
                    
                    <ListCell>
                      <ScoreCell risk={riskLevel}>
                        <ScoreValue>{assessment.totalScore}</ScoreValue>
                      </ScoreCell>
                    </ListCell>
                    
                    <ListCell>
                      <RiskBadgeList risk={riskLevel}>
                        {riskLabel}
                      </RiskBadgeList>
                    </ListCell>
                    
                    <ListCell>
                      <div style={{ color: '#0c4a6e', fontWeight: '700', fontSize: '14px' }}>
                        {getReadmissionProbability(assessment.totalScore)}
                      </div>
                    </ListCell>
                    
                    <ListCell>
                      {assessment.assessedBy || 'Unknown'}
                    </ListCell>
                    
                    <ListCell>
                      <DateCell>
                        {format(parseISO(assessment.createdAt), 'MMM dd, yyyy')}
                      </DateCell>
                    </ListCell>
                    
                    <ListCell>
                      <ActionsCell>
                        <ListActionButton
                          variant="view"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAssessment(assessment);
                          }}
                          title="View Details"
                        >
                          <Eye size={14} />
                        </ListActionButton>
                        <ListActionButton
                          variant="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/assessments/edit/${assessment.id}`);
                          }}
                          title="Edit Assessment"
                        >
                          <Edit size={14} />
                        </ListActionButton>
                        <ListActionButton
                          variant="download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPDF(assessment);
                          }}
                          title="Download PDF"
                        >
                          <FileDown size={14} />
                        </ListActionButton>
                        <ListActionButton
                          variant="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAssessment(assessment);
                          }}
                          title="Delete Assessment"
                        >
                          <Trash2 size={14} />
                        </ListActionButton>
                      </ActionsCell>
                    </ListCell>
                  </ListRow>
                );
              })}
            </AssessmentsListContainer>
          )}

          {totalPages > 1 && (
            <PaginationComponent
              currentPage={currentPage}
              totalItems={paginationData.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showPageSizeSelector={true}
              pageSizeOptions={[6, 12, 24, 48, 100]}
              showInfo={true}
            />
          )}
        </>
      )}

      {/* Assessment Details Modal would go here */}
        {selectedAssessment && (
          <AssessmentDetailsModal
            assessment={selectedAssessment}
            onClose={() => setSelectedAssessment(null)}
            onDelete={() => {
              handleDeleteAssessment(selectedAssessment);
            }}
          />
        )}
    </AssessmentsContainer>
  );
};

export default AssessmentsList;
